import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext'; // Use our original context
import { supabase } from '../../supabaseClient'; // Use base client for logout
import { Bars3Icon } from '@heroicons/react/24/outline'; // Import hamburger icon
import OrganizationSwitcher from '../admin/OrganizationSwitcher';

// Define props for the Header component
interface HeaderProps {
  toggleMobileMenu: () => void;
}

const Header: React.FC<HeaderProps> = ({ toggleMobileMenu }) => {
  // Get data directly from our context
  const { profile, user, loading, isSuperAdmin, viewingOrganizationId, isImpersonating } = useAuth();
  const [viewingOrgName, setViewingOrgName] = useState<string | null>(null);
  const [regularUserOrgName, setRegularUserOrgName] = useState<string | null>(null);
  const [orgNameLoading, setOrgNameLoading] = useState<boolean>(false);

  // Fetch the viewing organization name when viewingOrganizationId changes
  useEffect(() => {
    const fetchViewingOrgName = async () => {
      if (viewingOrganizationId) {
        try {
          const { data, error } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', viewingOrganizationId)
            .maybeSingle();

          if (error) {
            console.error('[Header] Error fetching viewing org name:', error);
            setViewingOrgName(null);
          } else if (data) {
            setViewingOrgName(data.name);
          } else {
            setViewingOrgName(null);
          }
        } catch (err) {
          console.error('[Header] Exception fetching viewing org name:', err);
          setViewingOrgName(null);
        }
      } else {
        setViewingOrgName(null);
      }
    };

    fetchViewingOrgName();
  }, [viewingOrganizationId]);

  // Fetch regular user's organization name from organizations table
  useEffect(() => {
    const fetchRegularUserOrgName = async () => {
      // Only fetch for regular users (not Super Admins when impersonating)
      if (profile && profile.organization_id && (!isSuperAdmin || !isImpersonating)) {
        const effectiveOrgId = profile.organization_id;
        setOrgNameLoading(true);

        try {
          const { data, error } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', effectiveOrgId)
            .maybeSingle();

          if (error) {
            console.error('[Header] Error fetching regular user org name:', error);
            setRegularUserOrgName(null);
          } else if (data) {
            setRegularUserOrgName(data.name);
          } else {
            setRegularUserOrgName(null);
          }
        } catch (err) {
          console.error('[Header] Exception fetching regular user org name:', err);
          setRegularUserOrgName(null);
        } finally {
          setOrgNameLoading(false);
        }
      } else {
        setRegularUserOrgName(null);
        setOrgNameLoading(false);
      }
    };

    fetchRegularUserOrgName();
  }, [profile?.organization_id, isSuperAdmin, isImpersonating]);

  const handleLogout = async () => {
    await supabase.auth.signOut({ scope: 'local' });
  };

  // Determine display values based on context state
  let companyDisplay = 'N/A';
  let userDisplay = 'N/A';

  if (loading) {
    // Show loading only if context indicates initial load is happening
    companyDisplay = 'Loading...';
    userDisplay = 'Loading...';
  } else if (isSuperAdmin && isImpersonating && viewingOrgName) {
    // Super admin viewing another org - show that org's name
    companyDisplay = viewingOrgName;
    userDisplay = profile?.full_name || user?.email || 'N/A';
  } else if (isSuperAdmin && !isImpersonating) {
    // Super admin in Platform Overview mode
    companyDisplay = 'Platform Overview';
    userDisplay = profile?.full_name || user?.email || 'N/A';
  } else if (profile) {
    // Regular user or super admin viewing their own org - use fetched org name
    if (orgNameLoading) {
      companyDisplay = 'Loading...';
    } else {
      companyDisplay = regularUserOrgName || 'N/A';
    }
    userDisplay = profile.full_name || user?.email || 'N/A'; // Use profile name, fallback to user email
  } else if (user) {
    // If no profile but user exists, show email
    userDisplay = user.email || 'N/A';
  }
  // If no profile and no user (shouldn't happen in protected routes), defaults to N/A

  return (
    <header className="bg-surface-card shadow-sm border-b border-surface-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 gap-2">
          {/* Left side - Hamburger button for mobile */}
          <div className="flex items-center shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="md:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 rounded-md text-text-muted hover:text-text-muted hover:bg-surface-bg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent-blue"
              aria-label="Toggle mobile menu"
            >
              <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
            </button>
            {/* Placeholder for potential future desktop elements on the left */}
            <div className="hidden md:flex items-center">
              {/* Desktop placeholder */}
            </div>
          </div>

          {/* Right side - Company, User, Logout */}
          <div className="flex flex-1 min-w-0 items-center justify-end space-x-2 md:space-x-4">
            {isSuperAdmin && <div className="shrink-0"><OrganizationSwitcher /></div>}
             {/* Company - hide label on mobile, keep on desktop. UP-0j: shrinks with an ellipsis; full name on hover */}
             <span className="text-sm text-text-body min-w-0 truncate" title={companyDisplay}>
               <span className="hidden md:inline">Company: </span>
               <span className="font-medium">{companyDisplay}</span>
             </span>
             {/* User - hide completely on mobile, visible on desktop */}
             <span className="text-sm text-text-body hidden md:inline min-w-0 truncate" title={userDisplay}>
               User: <span className="font-medium">{userDisplay}</span>
             </span>
             {/* Logout - hide on mobile, visible on desktop */}
             <button
                onClick={handleLogout}
                className="text-sm text-text-muted hover:text-text-body underline hidden md:block shrink-0 whitespace-nowrap"
             >
                Logout
             </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

export {}; // For isolatedModules
